import { CartItem, CartItemMetaData } from '../types/cart'
import { Product, ProductPriceResponse } from '../types/product'

// ---------------------------------------------------------------------------
// Raw package-weight formatting (e.g. "250g" -> "250 г")
// ---------------------------------------------------------------------------
const PACKAGE_WEIGHT_DISPLAY: Record<string, string> = {
  '250g': '250 г',
  '500g': '500 г',
  '1000g': '1 кг',
  '1kg': '1 кг',
}

export const formatPackageWeight = (value: string): string => {
  return PACKAGE_WEIGHT_DISPLAY[value] || value
}

// ---------------------------------------------------------------------------
// Variant helpers (new prices[] API)
// ---------------------------------------------------------------------------

/** Sort variants by displayOrder (nulls last), preserving original order as
 *  tiebreak. */
export const getSortedVariants = (prices: ProductPriceResponse[]): ProductPriceResponse[] => {
  if (!prices || prices.length === 0) return []
  return [...prices].sort((a, b) => {
    const aOrder = a.displayOrder ?? Infinity
    const bOrder = b.displayOrder ?? Infinity
    return aOrder - bOrder
  })
}

/** Round a monetary value to the two decimal places used by the storefront. */
export const roundCurrency = (value: number): number => {
  return Math.round(Number(`${value}e2`)) / 100
}

/** Normalize an API catalog discount to a safe percentage. */
export const getDiscountPercent = (discount?: number | null): number => {
  if (typeof discount !== 'number' || !Number.isFinite(discount) || discount <= 0) return 0
  return Math.min(discount, 100)
}

export const hasCatalogDiscount = (discount?: number | null): boolean => {
  return getDiscountPercent(discount) > 0
}

/** Hide sub-1% badges — they look like rounding errors on cards. */
export const MIN_VISIBLE_DISCOUNT_PERCENT = 1

export const shouldShowDiscountBadge = (discount?: number | null): boolean => {
  return getDiscountPercent(discount) >= MIN_VISIBLE_DISCOUNT_PERCENT
}

export const formatDiscountPercent = (discount?: number | null): string => {
  return getDiscountPercent(discount).toLocaleString('uk-UA', {
    maximumFractionDigits: 2,
  })
}

/** Effective (sale) price for a single variant. */
export const getEffectivePrice = (variant: ProductPriceResponse): number => {
  const discountPercent = getDiscountPercent(variant.discount)
  return roundCurrency(variant.price * (1 - discountPercent / 100))
}

/** Monetary discount for analytics and other currency-only integrations. */
export const getCatalogDiscountAmount = (variant: ProductPriceResponse): number => {
  return roundCurrency(variant.price - getEffectivePrice(variant))
}

/** First sorted variant, or null when prices[] is empty. */
export const getDefaultVariant = (prices: ProductPriceResponse[]): ProductPriceResponse | null => {
  const sorted = getSortedVariants(prices)
  return sorted.length > 0 ? sorted[0] : null
}

/** Minimum effective price across all variants, or null when prices[] is empty. */
export const getMinEffectivePrice = (prices: ProductPriceResponse[]): number | null => {
  if (!prices || prices.length === 0) return null
  return Math.min(...prices.map(getEffectivePrice))
}

/** Variant that produces the minimum effective price, or null when empty. */
export const getMinEffectiveVariant = (
  prices: ProductPriceResponse[],
): ProductPriceResponse | null => {
  if (!prices || prices.length === 0) return null
  return prices.reduce((minimum, variant) =>
    getEffectivePrice(variant) < getEffectivePrice(minimum) ? variant : minimum,
  )
}

/** Human-readable label for a variant pill.
 *  - drip:    "12г · 7 шт"
 *  - capsule: "80 г · 10 капсул"
 *  - coffee:  "250 г" (formatted)
 */
export const getVariantLabel = (product: Product, variant: ProductPriceResponse): string => {
  const baseLabel = variant.weightLabel || formatPackageWeight(variant.weight || '')
  if (variant.quantity) {
    if (isCapsuleProduct(product)) {
      return `${baseLabel} · ${variant.quantity} капсул`
    }
    return `${baseLabel} · ${variant.quantity} шт`
  }
  return baseLabel
}

// ---------------------------------------------------------------------------
// Product-type detection (now based only on productCategory / names)
// ---------------------------------------------------------------------------
const normalizeCat = (value?: string | null) => (value || '').trim().toLowerCase()

export const isCapsuleProduct = (product: Product): boolean => {
  const values = [product.productCategory, product.categoryInternalName, product.categoryExternalName]
    .map(normalizeCat)
  return values.some(v => v === 'kapsuli' || v.includes('капсул') || v.includes('capsule'))
}

export const isDripProduct = (product: Product): boolean => {
  const values = [product.productCategory, product.categoryInternalName, product.categoryExternalName]
    .map(normalizeCat)
  return values.some(v => v === 'drip' || v === 'dripi' || v.includes('дріп'))
}

// ---------------------------------------------------------------------------
// Display weight for the price row  "/ 250 г", "/ 12г · 7 шт", …
// ---------------------------------------------------------------------------
export const getProductDisplayWeight = (
  product: Product,
  variant?: ProductPriceResponse | null,
): string => {
  const v = variant || getDefaultVariant(product.prices)
  if (!v) return ''
  return getVariantLabel(product, v)
}

// ---------------------------------------------------------------------------
// Formatted packaging value sent to the order API (packageWeight field)
// ---------------------------------------------------------------------------
export const getPackagingValue = (
  product: Product,
  variant?: ProductPriceResponse | null,
): string => {
  const v = variant || getDefaultVariant(product.prices)
  if (!v) return ''

  const isCapsule = isCapsuleProduct(product)
  const isDrip = isDripProduct(product) && !isCapsule

  if (isDrip && v.quantity) {
    return `${v.weightLabel || v.weight} × ${v.quantity}`
  }
  if (isCapsule) {
    return v.weightLabel || formatPackageWeight(v.weight || '')
  }
  // package coffee
  return formatPackageWeight(v.weight || '')
}

// ---------------------------------------------------------------------------
// Cart-item type detection (operates on CartItem with meta_data)
// ---------------------------------------------------------------------------
const matchesCapsuleValue = (value: string) => {
  const v = (value || '').trim().toLowerCase()
  return v === 'kapsuli' || v.includes('капсул') || v.includes('capsule')
}

const matchesDripValue = (value: string) => {
  const v = (value || '').trim().toLowerCase()
  return v === 'drip' || v === 'dripi' || v.includes('дріп')
}

export const isCapsuleCartItem = (item: CartItem): boolean => {
  const getMeta = (key: string) => item.meta_data?.find(m => m.key === key)?.value || ''
  const productCategory = getMeta('productCategory')
  const categoryInternalName = getMeta('categoryInternalName')
  const capsuleWeight = getMeta('capsuleWeight')
  const capsuleQuantity = getMeta('capsuleQuantity')
  return (
    matchesCapsuleValue(productCategory) ||
    matchesCapsuleValue(categoryInternalName) ||
    !!capsuleQuantity ||
    !!capsuleWeight ||
    /капсул|capsule/i.test(item.name)
  )
}

export const isDripCartItem = (item: CartItem): boolean => {
  if (isCapsuleCartItem(item)) return false
  const getMeta = (key: string) => item.meta_data?.find(m => m.key === key)?.value || ''
  const productCategory = getMeta('productCategory')
  const categoryInternalName = getMeta('categoryInternalName')
  const dripWeight = getMeta('dripWeight')
  const dripQuantity = getMeta('dripQuantity')
  const rawPackageWeight = getMeta('packageWeight')
  return (
    matchesDripValue(productCategory) ||
    matchesDripValue(categoryInternalName) ||
    (!!dripWeight && !!dripQuantity) ||
    /×/.test(rawPackageWeight)
  )
}

// ---------------------------------------------------------------------------
// Build meta_data for a cart item
// ---------------------------------------------------------------------------
export const buildProductCartMeta = (
  product: Product,
  options: { grindLabel?: string; variant?: ProductPriceResponse | null } = {},
): CartItemMetaData[] => {
  const variant = options.variant || getDefaultVariant(product.prices)

  const isCapsule = isCapsuleProduct(product)
  const isDrip = isDripProduct(product) && !isCapsule
  const isPackageCoffee = !isCapsule && !isDrip

  const meta: CartItemMetaData[] = [
    { key: 'productCategory', value: product.productCategory || '' },
    { key: 'categoryInternalName', value: product.categoryInternalName || '' },
    { key: 'packageWeight', value: getPackagingValue(product, variant) },
  ]

  if (isCapsule) {
    const capsuleWeightLabel = variant ? (variant.weightLabel || formatPackageWeight(variant.weight || '')) : ''
    const capsuleQty = variant?.quantity ? String(variant.quantity) : ''
    meta.push({ key: 'capsuleWeight', value: capsuleWeightLabel })
    meta.push({ key: 'capsuleQuantity', value: capsuleQty })
    meta.push({ key: 'grindType', value: capsuleQty })
  } else if (isDrip) {
    const dripWeightLabel = variant ? (variant.weightLabel || variant.weight || '') : ''
    const dripQty = variant?.quantity ? String(variant.quantity) : ''
    meta.push({ key: 'dripWeight', value: dripWeightLabel })
    meta.push({ key: 'dripQuantity', value: dripQty })
  } else if (isPackageCoffee && options.grindLabel) {
    meta.push({ key: 'grindType', value: options.grindLabel })
  }

  meta.push(
    { key: 'beanType', value: product.beanType?.label || '' },
    { key: 'beanVariety', value: product.beanVariety?.label || '' },
    { key: 'processingMethod', value: product.processingMethod?.label || '' },
    { key: 'originCountry', value: product.originCountry || '' },
    { key: 'originRegion', value: product.originRegion || '' },
  )

  return meta
}

/** Effective unit price for newly-created cart items. */
export const getCartItemUnitPrice = (item: CartItem): number => {
  if (typeof item.discount_percent !== 'number') {
    return roundCurrency(item.price)
  }

  const discountPercent = getDiscountPercent(item.discount_percent)
  return roundCurrency(item.regular_price * (1 - discountPercent / 100))
}

export const hasCartItemDiscount = (item: CartItem): boolean => {
  return hasCatalogDiscount(item.discount_percent)
}

// ---------------------------------------------------------------------------
// Remaining cart utilities (unchanged)
// ---------------------------------------------------------------------------

export const exportCart = (cartItems: CartItem[]): string => {
  return JSON.stringify(cartItems, null, 2)
}

export const importCart = (jsonString: string): CartItem[] => {
  try {
    return JSON.parse(jsonString)
  } catch (error) {
    return []
  }
}

export const validateCartItem = (item: any): item is CartItem => {
  return (
    typeof item === 'object' &&
    item !== null &&
    typeof item.id === 'number' &&
    typeof item.name === 'string' &&
    typeof item.price === 'number' &&
    typeof item.regular_price === 'number' &&
    typeof item.image === 'string' &&
    typeof item.sku === 'string' &&
    typeof item.quantity === 'number' &&
    item.quantity > 0
  )
}

export const filterValidCartItems = (items: any[]): CartItem[] => {
  return items.filter(validateCartItem)
}

export const calculateTotalPrice = (cartItems: CartItem[]): number => {
  return roundCurrency(cartItems.reduce((sum, item) => {
    const price = getCartItemUnitPrice(item)
    return sum + price * item.quantity
  }, 0))
}

export const calculateTotalQuantity = (cartItems: CartItem[]): number => {
  return cartItems.reduce((sum, item) => sum + item.quantity, 0)
}

export const calculateSavings = (cartItems: CartItem[]): number => {
  return roundCurrency(cartItems.reduce((sum, item) => {
    if (hasCartItemDiscount(item)) {
      return sum + (item.regular_price - getCartItemUnitPrice(item)) * item.quantity
    }
    return sum
  }, 0))
}

export const groupItemsByCategory = (cartItems: CartItem[]): Record<string, CartItem[]> => {
  return cartItems.reduce((groups, item) => {
    const category = item.meta_data?.find(m => m.key === 'category')?.value || 'uncategorized'
    if (!groups[category]) {
      groups[category] = []
    }
    groups[category].push(item)
    return groups
  }, {} as Record<string, CartItem[]>)
}

export const sortItemsByPrice = (cartItems: CartItem[], ascending: boolean = true): CartItem[] => {
  return [...cartItems].sort((a, b) => {
    const priceA = getCartItemUnitPrice(a)
    const priceB = getCartItemUnitPrice(b)
    return ascending ? priceA - priceB : priceB - priceA
  })
}

export const sortItemsByName = (cartItems: CartItem[], ascending: boolean = true): CartItem[] => {
  return [...cartItems].sort((a, b) => {
    const nameA = a.name.toLowerCase()
    const nameB = b.name.toLowerCase()
    return ascending ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA)
  })
}

export const searchItemsByName = (cartItems: CartItem[], searchTerm: string): CartItem[] => {
  const term = searchTerm.toLowerCase()
  return cartItems.filter(item => item.name.toLowerCase().includes(term))
}

export const getUniqueCategories = (cartItems: CartItem[]): string[] => {
  const categories = cartItems
    .map(item => item.meta_data?.find(m => m.key === 'category')?.value)
    .filter(Boolean) as string[]
  return Array.from(new Set(categories))
}

export const isItemInCart = (cartItems: CartItem[], id: number, variationId?: number): boolean => {
  return cartItems.some(item => item.id === id && item.variationId === variationId)
}

export const getItemQuantity = (
  cartItems: CartItem[],
  id: number,
  variationId?: number,
): number => {
  const item = cartItems.find(item => item.id === id && item.variationId === variationId)
  return item ? item.quantity : 0
}

export const formatDeliveryAddress = (
  shippingAddress: {
    shippingStreet?: string
    shippingEntrance?: string
    shippingApartment?: string
  },
  deliveryType?: string,
  deliverySubtype?: string
): string => {
  if (deliveryType === 'Нова Пошта' && deliverySubtype === "Кур'єр") {
    let address = shippingAddress.shippingStreet || ''

    if (shippingAddress.shippingEntrance && shippingAddress.shippingEntrance.trim() !== '') {
      address += `, під'їзд ${shippingAddress.shippingEntrance}`
    }

    if (shippingAddress.shippingApartment && shippingAddress.shippingApartment.trim() !== '') {
      address += `, квартира ${shippingAddress.shippingApartment}`
    }

    return address
  }

  return shippingAddress.shippingStreet || ''
}
